import { useHeaderAdsProvider } from '@/context/HeaderAdsContext';
import { Layout, Space, TreeSelect, Radio, Divider, Button } from 'antd';
import React, {
  useEffect,
  useState,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { useLocation } from 'react-router-dom';
import FullscreenButton from '../screenToggle';
import DateRangePicker from '../date-range-picker/DateRangePicker';
import { ProfileDropDown } from '../layout/ProfileDropDown';
import CountrySwitcher from '../country/CountrySwitcher';
import NotificationBell from '../notifications/NotificationBell';
import GlobalSearch from '../GlobalSearch';

const { Header } = Layout;

export interface Account {
  id: number;
  createdAt: string;
  updatedAt: string;
  userId: number;
  profileId: string;
  countryCode: string;
  currencyCode: string;
  dailyBudget: number | null;
  timezone: string;
  marketplaceId: string;
  accountId: string;
  accountType: 'vendor' | 'seller' | string;
  accountName: string;
  isMarketingStreamActivate: boolean;
  validPaymentMethod: boolean;
  linkedAccountId: number;
  selected: boolean;
}

// This header is functionally identical to header2 but styled to mirror Insight 360
const DashboardHeaderAds: React.FC = () => {
  const {
    adAccounts,
    setAdAccounts,
    adAccountVisible,
    setAdAccountVisible,
    setAdvertiserId,
    setProfileId,
    setLinkedAccountId,
  } = useHeaderAdsProvider();

  const [selectedValue, setSelectedValue] = useState<string | undefined>(() => {
    // Try localStorage first
    try {
      const stored = localStorage.getItem('app_v2_ads_profiles');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const match = adAccounts
            .filter((account) => (account.profiles || []).length > 0)
            .flatMap((account) => account.profiles)
            .find((profile) => parsed.includes(profile.profileId));
          if (match) return match.accountId;
        }
      }
    } catch {
      console.error('Error parsing app_v2_ads_profiles from localStorage');
    }


    // Fall back to selected profile
    const selected = adAccounts
      .filter((account) => (account.profiles || []).length > 0)
      .flatMap((account) => account.profiles)
      .find((profile) => profile.selected);
    if (selected) return selected.accountId;

    // Finally use first profile
    const firstAccount = adAccounts.find(
      (account) => (account.profiles || []).length > 0
    );
    return firstAccount?.profiles[0]?.accountId;
  }); // committed selection (single)
  const [tempValue, setTempValue] = useState<string | undefined>(() => {
    // Try localStorage first
    try {
      const stored = localStorage.getItem('app_v2_ads_profiles');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const match = adAccounts
            .filter((account) => (account.profiles || []).length > 0)
            .flatMap((account) => account.profiles)
            .find((profile) => parsed.includes(profile.profileId));
          if (match) return match.accountId;
        }
      }
    } catch {
      console.error('Error parsing app_v2_ads_profiles from localStorage');
    }

    // Fall back to selected profile
    const selected = adAccounts
      .filter((account) => (account.profiles || []).length > 0)
      .flatMap((account) => account.profiles)
      .find((profile) => profile.selected);
    if (selected) return selected.accountId;

    // Finally use first profile
    const firstAccount = adAccounts.find(
      (account) => (account.profiles || []).length > 0
    );
    return firstAccount?.profiles[0]?.accountId;
  }); // UI selection prior to Apply
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [expandedAccounts, setExpandedAccounts] = useState<Set<number>>(
    new Set()
  );
  // One-shot guard to prevent hydrate/default effects from looping
  const hasHydratedRef = useRef(false);
  const location = useLocation();

  // Use all profiles (no marketing-stream filter) so users can switch any profile on ads
  const filteredAccounts = useMemo(() => {
    return adAccounts.filter((account) => (account.profiles || []).length > 0);
  }, [adAccounts]);

  // ADS localStorage key for persistence
  const ADS_PROFILES_KEY = 'app_v2_ads_profiles';

  // Hydrate from ADS localStorage on first load
  useEffect(() => {
    if (!filteredAccounts.length || hasUserInteracted || hasHydratedRef.current)
      return;

    let initialized = false;

    try {
      const stored = localStorage.getItem(ADS_PROFILES_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length > 0) {
          const match = filteredAccounts
            .flatMap((a) => a.profiles)
            .find((p) => parsed.includes(p.profileId)) as unknown as
            | Account
            | undefined;
          if (match) {
            const updated = filteredAccounts.map((account) => ({
              ...account,
              selected: account.profiles.some(
                (p) => p.profileId === match.profileId
              ),
              profiles: account.profiles.map((p) => ({
                ...p,
                selected: p.profileId === match.profileId,
              })),
            }));
            setAdAccounts(updated);
            setAdvertiserId([match.accountId]);
            setProfileId(match.profileId);
            setLinkedAccountId(match.linkedAccountId);
            setSelectedValue(match.accountId);
            setTempValue(match.accountId);
            hasHydratedRef.current = true;
            initialized = true;
          }
        }
      }
    } catch {
      console.error('Error hydrating ADS profiles from localStorage');
    }

    // If localStorage didn't have a valid match, initialize with first profile
    if (!initialized) {
      const firstProfile = filteredAccounts[0]
        ?.profiles[0] as unknown as Account;
      if (firstProfile) {
        const updatedAccounts = filteredAccounts.map((account) => ({
          ...account,
          selected: account.id === filteredAccounts[0].id,
          profiles: account.profiles.map((profile) => ({
            ...profile,
            selected: profile.accountId === firstProfile.accountId,
          })),
        }));
        setAdAccounts(updatedAccounts);
        setAdvertiserId([firstProfile.accountId]);
        setProfileId(firstProfile.profileId);
        setLinkedAccountId(firstProfile.linkedAccountId);
        setSelectedValue(firstProfile.accountId);
        setTempValue(firstProfile.accountId);

        // Persist to localStorage
        try {
          localStorage.setItem(
            ADS_PROFILES_KEY,
            JSON.stringify([firstProfile.profileId])
          );
        } catch {
          console.error('Error saving initial profile to localStorage');
        }

        hasHydratedRef.current = true;
      }
    }
  }, [
    filteredAccounts,
    hasUserInteracted,
    setAdAccounts,
    setAdvertiserId,
    setLinkedAccountId,
    setProfileId,
  ]);

  useEffect(() => {
    if (
      filteredAccounts.length > 0 &&
      !hasUserInteracted &&
      !hasHydratedRef.current
    ) {
      const selectedProfile = filteredAccounts
        .flatMap((account) => account.profiles)
        .find((profile) => profile.selected) as unknown as Account;

      if (selectedProfile) {
        setAdvertiserId([selectedProfile.accountId]);
        setProfileId(selectedProfile.profileId);
        setLinkedAccountId(selectedProfile.linkedAccountId);
        setSelectedValue(selectedProfile.accountId);
        setTempValue(selectedProfile.accountId);
      } else {
        const firstProfile = filteredAccounts[0]
          .profiles[0] as unknown as Account;
        if (firstProfile) {
          const updatedAccounts = filteredAccounts.map((account) => ({
            ...account,
            selected: account.id === filteredAccounts[0].id,
            profiles: account.profiles.map((profile) => ({
              ...profile,
              selected: profile.accountId === firstProfile.accountId,
            })),
          }));
          setAdAccounts(updatedAccounts);
          setAdvertiserId([firstProfile.accountId]);
          setProfileId(firstProfile.profileId);
          setLinkedAccountId(firstProfile.linkedAccountId);
          setSelectedValue(firstProfile.accountId);
          setTempValue(firstProfile.accountId);
        }
      }
    }
  }, [
    filteredAccounts,
    hasUserInteracted,
    setAdAccounts,
    setAdvertiserId,
    setLinkedAccountId,
    setProfileId,
  ]);

  useEffect(() => {
    if (
      filteredAccounts.length &&
      !selectedValue &&
      !hasUserInteracted &&
      !hasHydratedRef.current
    ) {
      const selectedProfile = filteredAccounts
        .flatMap((account) => account.profiles)
        .find((profile) => profile.selected);
      const defaultSelected =
        selectedProfile?.accountId ||
        filteredAccounts[0]?.profiles[0]?.accountId;
      if (defaultSelected) {
        setSelectedValue(defaultSelected);
        setTempValue(defaultSelected);
        if (!selectedProfile) {
          const updatedAccounts = filteredAccounts.map((account) => ({
            ...account,
            selected: account.profiles.some(
              (p) => p.accountId === defaultSelected
            ),
            profiles: account.profiles.map((profile) => ({
              ...profile,
              selected: profile.accountId === defaultSelected,
            })),
          }));
          setAdAccounts(updatedAccounts);
        }
      }
    }
  }, [filteredAccounts, hasUserInteracted, setAdAccounts, selectedValue]);

  const getPageTitle = useCallback(() => {
    const path = location.pathname.split('/').pop() || '';
    return path
      .split('-')
      .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
      .join(' ');
  }, [location.pathname]);

  // const getSelectedProfileName = useCallback(() => {
  //   if (!tempValue) return 'Select Profile';
  //   const selectedProfile = filteredAccounts
  //     .flatMap((account) => account.profiles)
  //     .find((profile) => profile.accountId === tempValue);
  //   return selectedProfile
  //     ? `${selectedProfile.accountName} (1)`
  //     : 'Select Profile';
  // }, [filteredAccounts, tempValue]);

  const treeData = useMemo(() => {
    return filteredAccounts.map((account) => ({
      title: `${account.name} (${account.profiles.length})`,
      value: `account_${account.id}`,
      selectable: false,
      children: account.profiles.map((profile) => ({
        title: profile.accountName,
        value: profile.accountId,
      })),
    }));
  }, [filteredAccounts]);

  const toggleAccountExpansion = useCallback((accountId: number) => {
    setExpandedAccounts((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  }, []);
  const applySelection = useCallback(
    (value: string | undefined) => {
      if (!value) return;
      const updatedAccounts = filteredAccounts.map((account) => ({
        ...account,
        selected: account.profiles.some((p) => value === p.accountId),
        profiles: account.profiles.map((profile) => ({
          ...profile,
          selected: value === profile.accountId,
        })),
      }));
      setAdAccounts(updatedAccounts);
      setAdvertiserId([value]);
      const firstSelected = updatedAccounts
        .flatMap((a) => a.profiles)
        .find((p) => p.selected) as unknown as Account | undefined;
      if (firstSelected) {
        setProfileId(firstSelected.profileId);
        setLinkedAccountId(firstSelected.linkedAccountId);
        // Persist to ADS key and notify same-tab listeners
        try {
          localStorage.setItem(
            ADS_PROFILES_KEY,
            JSON.stringify([firstSelected.profileId])
          );
          window.dispatchEvent(
            new CustomEvent('adsProfileChanged', {
              detail: { profileIds: [firstSelected.profileId] },
            })
          );
        } catch {
          console.error('Error persisting ADS profiles to localStorage');
        }
      }
    },
    [
      filteredAccounts,
      setAdAccounts,
      setAdvertiserId,
      setLinkedAccountId,
      setProfileId,
    ]
  );

  const collapseTriggerStyle: React.CSSProperties = {
    backgroundColor: '#232f3e',
    color: 'rgba(255, 255, 255, 0.65)',
    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
    height: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'color 0.3s',
    overflow: 'visible',
  };

  // Close dropdown when route changes to avoid overlay blocking navigation
  useEffect(() => {
    setAdAccountVisible(false);
  }, [location.pathname, setAdAccountVisible]);

  // Check if current route is /dashboard/ads/adsdata to hide dateRange
  const shouldHideDateRange = location.pathname.includes(
    '/dashboard/ads/adsdata'
  );

  return (
    <Header
      style={{
        ...collapseTriggerStyle,
        padding: '0 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <h2
        style={{
          margin: 0,
          fontSize: '1.2rem',
          fontWeight: 500,
          lineHeight: '1.5715',
          textTransform: 'uppercase',
          color: '#fff',
          letterSpacing: '1.5px',
        }}
      >
        {getPageTitle()}
      </h2>

      <div className="flex items-center gap-4">
        <FullscreenButton />

        {/* Global Search */}
        <GlobalSearch placeholder="Search pages and features..." />

        <Space size="large">
          {/* <FullscreenButton /> */}
          <TreeSelect
            className="custom-treeselect"
            style={{ width: 250 }}
            value={tempValue}
            styles={{ popup: { root: { maxHeight: 400, overflow: 'auto' } } }}
            treeData={treeData}
            placeholder="Select Profile"
            treeDefaultExpandAll={false}
            showSearch={false}
            onChange={() => {}}
            onSelect={() => {}}
            open={adAccountVisible}
            onOpenChange={(visible) => {
              // Avoid redundant state updates to prevent render loops
              if (visible !== adAccountVisible) setAdAccountVisible(visible);
            }}
            getPopupContainer={(trigger) =>
              (trigger.parentNode as HTMLElement) || document.body
            }
            popupRender={() => {
              return (
                <div>
                  <div style={{ padding: '8px 0' }}>
                    <Radio.Group
                      value={tempValue}
                      onChange={(e) => {
                        setTempValue(e.target.value);
                        setHasUserInteracted(true);
                      }}
                      style={{ width: '100%' }}
                    >
                      {filteredAccounts.map((account) => {
                        const isExpanded = expandedAccounts.has(account.id);
                        return (
                          <div key={account.id} style={{ marginBottom: 8 }}>
                            <div
                              style={{
                                fontWeight: 600,
                                padding: '8px 12px',
                                backgroundColor: '#f5f5f5',
                                fontSize: '13px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                userSelect: 'none',
                              }}
                              onClick={() => toggleAccountExpansion(account.id)}
                            >
                              {account.name} ({account.profiles.length})
                            </div>
                            {isExpanded &&
                              account.profiles.map((profile) => (
                                <div
                                  key={profile.accountId}
                                  style={{ padding: '4px 32px' }}
                                >
                                  <Radio value={profile.accountId}>
                                    {profile.accountName}
                                    <br /> <strong>{profile.profileId}</strong>
                                  </Radio>
                                </div>
                              ))}
                          </div>
                        );
                      })}
                    </Radio.Group>
                  </div>
                  <Divider style={{ margin: '4px 0' }} />
                  <div style={{ padding: 8 }}>
                    <Button
                      type="primary"
                      block
                      onClick={() => {
                        setSelectedValue(tempValue);
                        applySelection(tempValue);
                        setAdAccountVisible(false);
                      }}
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              );
            }}
          />
          {!shouldHideDateRange && <DateRangePicker />}
        </Space>
        <CountrySwitcher />
        <NotificationBell />
        <ProfileDropDown />
      </div>
    </Header>
  );
};

export default DashboardHeaderAds;
